'use client'

import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'

import { getClientFamilies } from '../../services/getFamilies/client/getClientFamilies'
import type { SizeGuideFamily } from '../../types'
import { getParsedOptions } from '../../utils/getParsedOptions/getParsedOptions'
import { useLines } from '../LinesProvider/LinesProvider'

interface Family {
	text: SizeGuideFamily['label']
	value: SizeGuideFamily['id']
}

type FamilyId = Family['value']

export interface FamiliesContextType {
	families: Family[]
	selectedFamilyId: string
	selectFamilyId: (familyId: FamilyId) => void
}

export interface FamiliesProviderProps {
	readonly children: React.ReactNode
	readonly initialFamilies: Family[]
}

export const FamiliesContext = createContext<FamiliesContextType | undefined>(
	undefined
)

export function FamiliesProvider({
	children,
	initialFamilies,
}: FamiliesProviderProps): React.ReactNode {
	const {
		country: { languageISO },
	} = useMasterData()

	const { selectedLineId } = useLines()

	const [families, setFamilies] = useState(initialFamilies)
	const [selectedFamilyId, setSelectedFamilyId] = useState<string>(
		families[0].value
	)
	const firstRenderRef = useRef(true)

	const selectFamilyId = (familyId: string): void => {
		setSelectedFamilyId(familyId)
	}

	useEffect(() => {
		const fetchFamilies = async () => {
			const { families: fetchedFamilies } = await getClientFamilies({
				language: languageISO,
				selectedLineId,
			})

			const familyOptions = getParsedOptions(fetchedFamilies)

			setFamilies(familyOptions)
			setSelectedFamilyId(familyOptions[0].value)
		}

		if (firstRenderRef.current) {
			firstRenderRef.current = false
		} else {
			fetchFamilies()
		}
	}, [selectedLineId])

	const familiesProviderValue = useMemo(
		() => ({ families, selectedFamilyId, selectFamilyId }),
		[families, selectedFamilyId, selectFamilyId]
	)

	return (
		<FamiliesContext.Provider value={familiesProviderValue}>
			{children}
		</FamiliesContext.Provider>
	)
}

/** @throws {Error} If useFamilies is used without FamiliesProvider */
export const useFamilies = (): FamiliesContextType => {
	const context = useContext(FamiliesContext)
	if (!context) {
		throw new Error('useFamilies must be used within a FamiliesProvider')
	}
	return context
}
